import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  SafeAreaView,
  ScrollView,
  Alert,
} from 'react-native';
import { useRoute, useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import type { RouteProp } from '@react-navigation/native';
import type { RootStackParamList } from '../navigation/RootNavigator';
import { useTranslation } from 'react-i18next';
import Icon from 'react-native-vector-icons/Ionicons';
import Clipboard from '@react-native-clipboard/clipboard';
import logger from '../utils/logger';
import { useTheme } from '../contexts/ThemeContext';

type ProvisionSuccessNavigationProp = StackNavigationProp<RootStackParamList, 'ProvisionSuccess'>;
type ProvisionSuccessRouteProp = RouteProp<RootStackParamList, 'ProvisionSuccess'>;

export default function ProvisionSuccessScreen() {
  const route = useRoute<ProvisionSuccessRouteProp>();
  const navigation = useNavigation<ProvisionSuccessNavigationProp>();
  const { t, i18n } = useTranslation();
  const { theme } = useTheme();
  const isRTL = i18n.language === 'ar';
  const styles = createStyles(theme);

  const { device, mqttPassword } = route.params;
  const [passwordCopied, setPasswordCopied] = useState(false);

  const handleCopyPassword = () => {
    Clipboard.setString(mqttPassword);
    setPasswordCopied(true);
    logger.log('📋 MQTT password copied to clipboard');

    Alert.alert(t('success'), t('password_copied_to_clipboard'), [{ text: t('ok') }]);
  };

  const handleFinish = () => {
    // Navigate back to devices list or dashboard
    navigation.reset({
      index: 0,
      routes: [{ name: 'Main' }],
    });
  };

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView contentContainerStyle={styles.content} showsVerticalScrollIndicator={false}>
        {/* Success Icon */}
        <View style={styles.iconContainer}>
          <View style={styles.successCircle}>
            <Icon name="checkmark-circle" size={120} color={theme.colors.success} />
          </View>
        </View>

        {/* Title */}
        <Text style={styles.title}>{t('device_added_successfully')}</Text>
        <Text style={styles.subtitle}>{t('device_provisioned_message')}</Text>

        {/* Device Info Card */}
        <View style={styles.card}>
          <View style={styles.cardHeader}>
            <Icon name="hardware-chip" size={24} color={theme.colors.info} />
            <Text style={styles.cardTitle}>{t('device_information')}</Text>
          </View>

          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>{t('device_name')}:</Text>
            <Text style={styles.infoValue}>{device.name}</Text>
          </View>

          {device.zone && (
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>{t('zone')}:</Text>
              <Text style={styles.infoValue}>{device.zone.name}</Text>
            </View>
          )}

          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>{t('status')}:</Text>
            <View style={styles.statusBadge}>
              <View style={styles.statusDot} />
              <Text style={styles.statusText}>{t('active')}</Text>
            </View>
          </View>
        </View>

        {/* MQTT Password Card */}
        <View style={styles.passwordCard}>
          <View style={styles.warningBanner}>
            <Icon name="warning" size={24} color={theme.colors.warning} />
            <Text style={styles.warningText}>{t('important_save_password')}</Text>
          </View>

          <Text style={styles.passwordLabel}>{t('mqtt_password')}</Text>
          <Text style={styles.passwordHelpText}>{t('password_shown_once')}</Text>

          <View style={styles.passwordBox}>
            <Text style={styles.passwordText}>{mqttPassword}</Text>
          </View>

          <TouchableOpacity
            style={[styles.copyButton, passwordCopied && styles.copyButtonSuccess]}
            onPress={handleCopyPassword}
          >
            <Icon name={passwordCopied ? 'checkmark-circle' : 'copy'} size={20} color="#fff" />
            <Text style={styles.copyButtonText}>
              {passwordCopied ? t('copied') : t('copy_password')}
            </Text>
          </TouchableOpacity>

          <View style={styles.instructionsBox}>
            <Text style={styles.instructionsTitle}>{t('next_steps')}:</Text>
            <View style={styles.instructionItem}>
              <Text style={styles.bulletPoint}>1.</Text>
              <Text style={styles.instructionText}>{t('save_password_secure_location')}</Text>
            </View>
            <View style={styles.instructionItem}>
              <Text style={styles.bulletPoint}>2.</Text>
              <Text style={styles.instructionText}>{t('configure_x280_mqtt_settings')}</Text>
            </View>
            <View style={styles.instructionItem}>
              <Text style={styles.bulletPoint}>3.</Text>
              <Text style={styles.instructionText}>{t('device_will_start_sending_data')}</Text>
            </View>
          </View>
        </View>

        {/* Connection Info Card */}
        <View style={styles.card}>
          <View style={styles.cardHeader}>
            <Icon name="information-circle" size={24} color={theme.colors.info} />
            <Text style={styles.cardTitle}>{t('connection_details')}</Text>
          </View>

          <View style={styles.connectionRow}>
            <Text style={styles.connectionLabel}>{t('mqtt_broker')}:</Text>
            <Text style={styles.connectionValue}>mqtt.iot-edge.io</Text>
          </View>

          <View style={styles.connectionRow}>
            <Text style={styles.connectionLabel}>{t('port')}:</Text>
            <Text style={styles.connectionValue}>1883</Text>
          </View>

          <View style={styles.connectionRow}>
            <Text style={styles.connectionLabel}>{t('username')}:</Text>
            <Text style={styles.connectionValue}>{device.mqttUsername}</Text>
          </View>

          <View style={styles.connectionRow}>
            <Text style={styles.connectionLabel}>{t('client_id')}:</Text>
            <Text style={styles.connectionValue}>{device.mqttClientId}</Text>
          </View>
        </View>

        <View style={{ height: 40 }} />
      </ScrollView>

      {/* Footer Button */}
      <View style={styles.footer}>
        <TouchableOpacity style={styles.finishButton} onPress={handleFinish}>
          <Text style={styles.finishButtonText}>{t('finish')}</Text>
          <Icon name={isRTL ? 'chevron-back' : 'chevron-forward'} size={24} color="#fff" />
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
}

const createStyles = (theme: any) =>
  StyleSheet.create({
    container: {
      flex: 1,
    },
    content: {
      paddingHorizontal: 16,
      paddingTop: 24,
      paddingBottom: 24,
    },
    iconContainer: {
      alignItems: 'center',
      marginBottom: 24,
    },
    successCircle: {
      alignItems: 'center',
      justifyContent: 'center',
    },
    title: {
      fontSize: 24,
      fontWeight: 'bold',
      color: theme.colors.text,
      textAlign: 'center',
      marginBottom: 8,
    },
    subtitle: {
      fontSize: 16,
      color: theme.colors.textSecondary,
      textAlign: 'center',
      marginBottom: 32,
    },
    card: {
      backgroundColor: theme.colors.card,
      borderRadius: 12,
      padding: 16,
      marginBottom: 16,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
      elevation: 3,
    },
    cardHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 16,
    },
    cardTitle: {
      fontSize: 18,
      fontWeight: '600',
      color: theme.colors.text,
      marginLeft: 8,
    },
    infoRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 8,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.border,
    },
    infoLabel: {
      fontSize: 14,
      color: theme.colors.textSecondary,
    },
    infoValue: {
      fontSize: 14,
      fontWeight: '600',
      color: theme.colors.text,
    },
    statusBadge: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: theme.colors.success + '20',
      paddingHorizontal: 12,
      paddingVertical: 4,
      borderRadius: 12,
    },
    statusDot: {
      width: 8,
      height: 8,
      borderRadius: 4,
      backgroundColor: theme.colors.success,
      marginRight: 6,
    },
    statusText: {
      fontSize: 12,
      fontWeight: '600',
      color: theme.colors.success,
    },
    passwordCard: {
      backgroundColor: theme.colors.card,
      borderRadius: 12,
      padding: 16,
      marginBottom: 16,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
      elevation: 3,
    },
    warningBanner: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: theme.colors.warning + '20',
      padding: 12,
      borderRadius: 8,
      marginBottom: 16,
    },
    warningText: {
      fontSize: 14,
      fontWeight: '600',
      color: theme.colors.warning,
      marginLeft: 8,
      flex: 1,
    },
    passwordLabel: {
      fontSize: 16,
      fontWeight: '600',
      color: theme.colors.text,
      marginBottom: 4,
    },
    passwordHelpText: {
      fontSize: 12,
      color: theme.colors.textSecondary,
      marginBottom: 12,
    },
    passwordBox: {
      backgroundColor: theme.colors.background,
      borderRadius: 8,
      padding: 16,
      borderWidth: 2,
      borderColor: theme.colors.info,
      borderStyle: 'dashed',
      marginBottom: 12,
    },
    passwordText: {
      fontSize: 16,
      fontFamily: 'monospace',
      color: theme.colors.text,
      textAlign: 'center',
      letterSpacing: 2,
    },
    copyButton: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.colors.info,
      borderRadius: 8,
      paddingVertical: 12,
      marginBottom: 16,
    },
    copyButtonSuccess: {
      backgroundColor: theme.colors.success,
    },
    copyButtonText: {
      fontSize: 16,
      fontWeight: '600',
      color: '#fff',
      marginLeft: 8,
    },
    instructionsBox: {
      backgroundColor: theme.colors.info + '20',
      borderRadius: 8,
      padding: 16,
    },
    instructionsTitle: {
      fontSize: 14,
      fontWeight: '600',
      color: theme.colors.info,
      marginBottom: 12,
    },
    instructionItem: {
      flexDirection: 'row',
      marginBottom: 8,
    },
    bulletPoint: {
      fontSize: 14,
      fontWeight: '600',
      color: theme.colors.info,
      marginRight: 8,
      width: 20,
    },
    instructionText: {
      fontSize: 14,
      color: theme.colors.text,
      flex: 1,
    },
    connectionRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      paddingVertical: 8,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.border,
    },
    connectionLabel: {
      fontSize: 14,
      color: theme.colors.textSecondary,
    },
    connectionValue: {
      fontSize: 14,
      fontFamily: 'monospace',
      color: theme.colors.text,
      fontWeight: '600',
    },
    footer: {
      padding: 16,
      backgroundColor: theme.colors.card,
      borderTopWidth: 1,
      borderTopColor: theme.colors.border,
    },
    finishButton: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.colors.success,
      borderRadius: 12,
      paddingVertical: 16,
    },
    finishButtonText: {
      fontSize: 18,
      fontWeight: '600',
      color: '#fff',
      marginRight: 8,
    },
  });
